import React from 'react'
import {
  XMarkIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'

interface CostEstimate {
  state: string
  keywords: string[]
  city_count: number
  consolidated_keywords?: number
  keyword_count?: number
  method?: string
  estimates: {
    optimistic: {
      total: number
      breakdown: {
        text_search?: { calls: number; cost: number }
        nearby_search?: { calls: number; cost: number }
        place_details: { calls: number; cost: number }
      }
      assumptions: {
        total_stores?: number
        total_stores_google?: number
        total_stores_with_osm?: number
        unique_stores: number
        new_stores: number
        cache_hit_rate: number
        duplicate_rate: number
        avg_pages_per_search?: number
        osm_contribution?: number
      }
    }
    realistic: {
      total: number
      breakdown: {
        text_search?: { calls: number; cost: number }
        nearby_search?: { calls: number; cost: number }
        place_details: { calls: number; cost: number }
      }
      assumptions: {
        total_stores?: number
        total_stores_google?: number
        total_stores_with_osm?: number
        unique_stores: number
        new_stores: number
        cache_hit_rate: number
        duplicate_rate: number
        avg_pages_per_search?: number
        osm_contribution?: number
      }
    }
    pessimistic: {
      total: number
      breakdown: {
        text_search?: { calls: number; cost: number }
        nearby_search?: { calls: number; cost: number }
        place_details: { calls: number; cost: number }
      }
      assumptions: {
        total_stores?: number
        total_stores_google?: number
        total_stores_with_osm?: number
        unique_stores: number
        new_stores: number
        cache_hit_rate: number
        duplicate_rate: number
        avg_pages_per_search?: number
        osm_contribution?: number
      }
    }
  }
  assumptions: {
    text_search_cost_per_1000?: number
    nearby_search_cost_per_1000?: number
    place_details_cost_per_1000: number
    note: string
  }
}

interface CostEstimateModalProps {
  isOpen: boolean
  onClose: () => void
  onProceed: () => void
  estimate: CostEstimate | null
  isLoading?: boolean
}

export default function CostEstimateModal({
  isOpen,
  onClose,
  onProceed,
  estimate,
  isLoading = false,
}: CostEstimateModalProps) {
  if (!isOpen) return null

  const scenarios = [
    {
      name: 'Optimistic',
      data: estimate?.estimates.optimistic,
      color: 'green',
      description: 'Best case scenario with high cache hit rate',
    },
    {
      name: 'Realistic',
      data: estimate?.estimates.realistic,
      color: 'blue',
      description: 'Most likely scenario based on historical data',
    },
    {
      name: 'Pessimistic',
      data: estimate?.estimates.pessimistic,
      color: 'red',
      description: 'Worst case scenario with low cache hit rate',
    },
  ]

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'green':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          text: 'text-green-800 dark:text-green-200',
          accent: 'text-green-600 dark:text-green-400',
        }
      case 'blue':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          text: 'text-blue-800 dark:text-blue-200',
          accent: 'text-blue-600 dark:text-blue-400',
        }
      case 'red':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          text: 'text-red-800 dark:text-red-200',
          accent: 'text-red-600 dark:text-red-400',
        }
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-900/20',
          border: 'border-gray-200 dark:border-gray-800',
          text: 'text-gray-800 dark:text-gray-200',
          accent: 'text-gray-600 dark:text-gray-400',
        }
    }
  }

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex min-h-screen items-center justify-center p-4'>
        <div
          className='fixed inset-0 bg-black bg-opacity-50 transition-opacity'
          onClick={onClose}
        />

        <div className='relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto'>
          {/* Header */}
          <div className='flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700'>
            <div className='flex items-center space-x-3'>
              <CurrencyDollarIcon className='h-6 w-6 text-blue-600 dark:text-blue-400' />
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                Cost Estimation
              </h3>
            </div>
            <button
              onClick={onClose}
              className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors'
            >
              <XMarkIcon className='h-6 w-6' />
            </button>
          </div>

          {/* Content */}
          <div className='p-6'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
                <span className='ml-3 text-gray-600 dark:text-gray-400'>
                  Calculating costs...
                </span>
              </div>
            ) : estimate ? (
              <div className='space-y-6'>
                {/* Job Summary */}
                <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
                  <h4 className='font-medium text-gray-900 dark:text-white mb-2'>
                    Job Summary
                  </h4>
                  <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-sm'>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        State:
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        {estimate.state}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        Keywords:
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        {estimate.keywords.length}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        Cities:
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        {estimate.city_count}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 dark:text-gray-400'>
                        {estimate.method === 'city_by_city_nearby_search'
                          ? 'Method:'
                          : 'Consolidated:'}
                      </span>
                      <span className='ml-2 font-medium text-gray-900 dark:text-white'>
                        {estimate.method === 'city_by_city_nearby_search'
                          ? 'City-by-City'
                          : estimate.consolidated_keywords || estimate.keyword_count}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cost Scenarios */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                  {scenarios.map((scenario) => {
                    const colors = getColorClasses(scenario.color)
                    return (
                      <div
                        key={scenario.name}
                        className={`${colors.bg} ${colors.border} border rounded-lg p-4`}
                      >
                        <div className='flex items-center justify-between mb-3'>
                          <h5 className={`font-semibold ${colors.text}`}>
                            {scenario.name}
                          </h5>
                          <span
                            className={`text-2xl font-bold ${colors.accent}`}
                          >
                            ${scenario.data?.total.toFixed(2)}
                          </span>
                        </div>

                        <p className={`text-sm ${colors.accent} mb-4`}>
                          {scenario.description}
                        </p>

                        <div className='space-y-2'>
                          {scenario.data?.breakdown.text_search && (
                            <div className='flex justify-between text-sm'>
                              <span className='text-gray-600 dark:text-gray-400'>
                                Text Search:
                              </span>
                              <span className='font-medium text-gray-900 dark:text-white'>
                                {scenario.data.breakdown.text_search.calls} calls
                                = $
                                {scenario.data.breakdown.text_search.cost.toFixed(
                                  2
                                )}
                              </span>
                            </div>
                          )}
                          {scenario.data?.breakdown.nearby_search && (
                            <div className='flex justify-between text-sm'>
                              <span className='text-gray-600 dark:text-gray-400'>
                                Nearby Search:
                              </span>
                              <span className='font-medium text-gray-900 dark:text-white'>
                                {scenario.data.breakdown.nearby_search.calls} calls
                                = $
                                {scenario.data.breakdown.nearby_search.cost.toFixed(
                                  2
                                )}
                              </span>
                            </div>
                          )}
                          <div className='flex justify-between text-sm'>
                            <span className='text-gray-600 dark:text-gray-400'>
                              Place Details:
                            </span>
                            <span className='font-medium text-gray-900 dark:text-white'>
                              {scenario.data?.breakdown.place_details.calls}{' '}
                              calls = $
                              {scenario.data?.breakdown.place_details.cost.toFixed(
                                2
                              )}
                            </span>
                          </div>
                        </div>

                        <div className='mt-4 pt-3 border-t border-gray-200 dark:border-gray-600'>
                          <div className='text-xs text-gray-500 dark:text-gray-400 space-y-1'>
                            <div>
                              Cache Hit Rate:{' '}
                              {(
                                scenario.data?.assumptions.cache_hit_rate * 100
                              ).toFixed(0)}
                              %
                            </div>
                            <div>
                              Duplicate Rate:{' '}
                              {(
                                scenario.data?.assumptions.duplicate_rate * 100
                              ).toFixed(0)}
                              %
                            </div>
                            <div>
                              New Places:{' '}
                              {scenario.data?.assumptions.new_stores}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Assumptions */}
                <div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4'>
                  <div className='flex items-center space-x-2 mb-2'>
                    <ChartBarIcon className='h-5 w-5 text-blue-600 dark:text-blue-400' />
                    <h4 className='font-medium text-blue-900 dark:text-blue-200'>
                      Assumptions
                    </h4>
                  </div>
                  <div className='text-sm text-blue-800 dark:text-blue-300'>
                    <p className='mb-2'>{estimate.assumptions.note}</p>
                    <div className='grid grid-cols-2 gap-4'>
                      {estimate.assumptions.text_search_cost_per_1000 && (
                        <div>
                          <span className='font-medium'>Text Search:</span> $
                          {estimate.assumptions.text_search_cost_per_1000}/1000
                          calls
                        </div>
                      )}
                      {estimate.assumptions.nearby_search_cost_per_1000 && (
                        <div>
                          <span className='font-medium'>Nearby Search:</span> $
                          {estimate.assumptions.nearby_search_cost_per_1000}/1000
                          calls
                        </div>
                      )}
                      <div>
                        <span className='font-medium'>Place Details:</span> $
                        {estimate.assumptions.place_details_cost_per_1000}/1000
                        calls
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className='text-center py-12'>
                <CurrencyDollarIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                <p className='text-gray-500 dark:text-gray-400'>
                  No cost estimate available
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className='flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700'>
            <button
              onClick={onClose}
              className='px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            >
              Cancel
            </button>
            <button
              onClick={onProceed}
              disabled={!estimate || isLoading}
              className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Proceed with Job
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
